import IconArrowRight from "assets/img/icon-arrow-right.svg?react"
import IconTrashBin from "assets/img/icon-trash-bin.svg?react"
import IconDropdownIndicator from "assets/img/icon-dropdown-indicator.svg?react"

import React, { FC, useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import cn from "classnames"

import Button from "components/Button/Button"
import Spinner from "components/Spinner/Spinner"
import Modal from "components/Modal/Modal"
import Form from "components/Form/Form"
import ShippingOrder, { ShippingOrderType } from "models/ShippingOrder"
import useShippingOrderEditModal, { ShippingOrderEditTab } from "./hooks/useShippingOrderEditModal"
import Checkbox from "components/Checkbox/Checkbox"
import ContinueSessionModal from "components/ContinueSessionModal/ContinueSessionModal"
import { EditSessionConfirmModalDuration, EditSessionDuration, ErrorCode } from "config/constants"
import AlertModal from "components/AlertModal/AlertModal"
import ConfirmModal from "components/ConfirmModal/ConfirmModal"
import Reservation from "models/Reservation"
import SelectAssetsForShippingOrderModal from "./SelectAssetsForShippingOrderModal"
import { getShippingOrderTypeLabel } from "utils/utils"
import ShippingOrderModalAssets from "./ShippingOrderModalAssets"
import UpdateHomebaseModal from "./UpdateHomebaseModal"
import SelectFreightUnitsModal, { ISelectedFreightUnit } from "./SelectFreightUnitsModal"
import { Flex, FlexItem } from "components/Flex"
import { InfoDropdown, InfoDropdownList, InfoDropdownListItemButton } from "components/InfoDropdown"
import ShippingOrderModalBulkLocations from "./ShippingOrderModalBulkLocations"
import ShippingOrderAddLocationModal, { IShippingOrderAddLocationModalProps } from "./ShippingOrderAddLocationModal"
import { IUpdateShippingOrderCartAssetDto } from "models/ShippingOrderCartAsset"
import useMedia from "hooks/useMedia"
import Tabs from "components/Tabs/Tabs"
import TabsItem from "components/Tabs/TabsItem/TabsItem"
import ShippingOrderEditModalBasicData from "./ShippingOrderEditModalBasicData"
import ShippingOrderCart from "models/ShippingOrderCart"
import { Guid } from "@/types"

export interface IShippingOrderEditModalProps {
    reservation: Reservation
    shippingOrder?: ShippingOrder | null
    shippingOrderId?: Guid
    type?: ShippingOrderType
    hideForwardToShippingControls?: boolean
    hidden?: boolean
    onClose(): void
    onSave?(params: { forwardToShipping: boolean, freightUnits: ISelectedFreightUnit[] | null }): Promise<void> | void
    onSaved?(): void
    onInitialized?(params: { addAssets: (assetsToAdd: Guid[]) => Promise<ShippingOrderCart> }): void
    onSessionExpired?(): void
}

const ShippingOrderEditModal: FC<React.PropsWithChildren<IShippingOrderEditModalProps>> = ({
    reservation,
    shippingOrder,
    shippingOrderId,
    type,
    hideForwardToShippingControls,
    hidden,
    onClose,
    onSave,
    onSaved,
    onInitialized,
    onSessionExpired,
}) => {
    const { t } = useTranslation()

    const {
        cart,
        isCartBusy,
        note,
        deliveryTerm,
        responsibleWarehouse,
        dates,
        personInCharge,
        personInChargePhone,
        forwardToShipping,
        setForwardToShipping,
        isDeliveryTermRequired,
        isDepartureAndArrivalDateRequired,
        isInitCartPending,
        isSaving,
        isSubmitted,
        onSaveClick: handleSubmit,
        validationErrors,
        errorTabs,
        handleClose,
        dataLossWarningEnabled,
        showContinueSessionModal,
        resetSessionTimeout,
        leaveWithoutConfirm,
        handleSessionExpired,
        errorCode,
        errorEmail,
        errorDate,
        setOverrideOwnership,
        deliveryTerms,
        tenantWarehouses,
        showSelectAssetsModal,
        setShowSelectAssetsModal,
        shippingOrderAssetIds,
        onAssetsSelect,
        handleDeleteAsset,
        handleDeleteAssets,
        isForwardToShippingCheckboxShown,
        isSaveAndPrepareLoadingListButtonShown,
        isForwardToShippingCheckboxDisabled,
        isSaveAndPrepareLoadingListButtonDisabled,
        handleBasicDataChange,
        createRequestLocation,
        changeAssetDepartureArrivalLocation,
        showUpdateHomebaseModal,
        setShowUpdateHomebaseModal,
        assetsWithFreightUnits,
        setAssetsWithFreightUnits,
        freightUnitsToCreate,
        setFreightUnitsToCreate,
        handleFreightUnitsSelection,
        updateAssetsHomebase,
        requestLocations,
        sort,
        setSort,
        shippingOrderAssetsExtended,
        isLoadingAssetDisplayStatuses,
        assetsToUpdateHomebase,
        selectedAssetIds,
        onAssetSelectionChange,
        canAddAssets,
        canDeleteAssets,
        canChangeAssetLocations,
        deliveryNoteHeaderText,
        deliveryNoteFooterText,
        deliveryNotePrintFeatures,
    } = useShippingOrderEditModal({
        reservation,
        shippingOrder,
        shippingOrderId,
        type,
        hideForwardToShippingControls,
        onClose,
        onSave,
        onSaved,
        onInitialized,
        onSessionExpired,
    })

    const [currentTab, setCurrentTab] = useState<ShippingOrderEditTab>(ShippingOrderEditTab.basicData)
    const [addLocationParams, setAddLocationParams] = useState<IShippingOrderAddLocationModalProps["params"]>(null)

    const modalTitle = `${t((shippingOrder || shippingOrderId) ? 'shippingOrders.editShippingOrder' : 'shippingOrders.addShippingOrder')}${cart?.type ? ` – ${getShippingOrderTypeLabel(cart.type)}` : ''}`

    const handleAddLocationModalClose = useCallback(() => {
        setAddLocationParams(null)
    }, [])

    const handleAddLocationClickInBulk = useCallback((isUnloading: boolean) => {
        setAddLocationParams({
            assetIds: selectedAssetIds,
            isUnloading,
        })
    }, [selectedAssetIds])

    const handleSelectLocationClickInBulk = useCallback(async (changes: Pick<IUpdateShippingOrderCartAssetDto, "departureLocationId" | "arrivalLocationId">) => {
        await changeAssetDepartureArrivalLocation(selectedAssetIds, changes)
    }, [changeAssetDepartureArrivalLocation, selectedAssetIds])

    const handleDeleteAssetsClickInBulk = useCallback(async () => {
        await handleDeleteAssets(selectedAssetIds)
        onAssetSelectionChange([])
    }, [handleDeleteAssets, onAssetSelectionChange, selectedAssetIds])

    const isLargeScreen = useMedia(["(min-width: 1441px)"], [true], false)

    return (
        <>
            {!errorCode &&
                <Modal
                    title={modalTitle}
                    onClose={isSaving ? undefined : handleClose}
                    maxWidth="1440px"
                    dataLossWarning={dataLossWarningEnabled}
                    hidden={hidden}
                    footer={close => (!isInitCartPending &&
                        <Flex gap="2.4rem" alignItems="end" justifyContent="space-between">
                            <FlexItem shrink={0}>
                                {currentTab === ShippingOrderEditTab.assets &&
                                    <Flex
                                        flow={isLargeScreen ? "row" : "column-reverse"}
                                        alignItems={isLargeScreen ? "center" : "flex-start"}
                                        gap="2.4rem">
                                        {canAddAssets && shippingOrderAssetIds.length > 0 &&
                                            <Button
                                                theme="secondary"
                                                label={t('shippingOrders.addAssetsToShippingOrder')}
                                                disabled={isCartBusy}
                                                onClick={() => setShowSelectAssetsModal(true)}
                                            />
                                        }
                                        {selectedAssetIds.length > 0 && (canChangeAssetLocations || canDeleteAssets) &&
                                            <div className={selectedAssetIds.length < 2 ? "d-none" : undefined}>
                                                <InfoDropdown
                                                    minWidth="24rem"
                                                    maxWidth="24rem"
                                                    placement="bottom-start"
                                                    label={isOpen => (
                                                        <Button
                                                            theme="link"
                                                            label={`${t('shippingOrders.editSelectedAssets')} (${selectedAssetIds.length})`}
                                                            disabled={isCartBusy}
                                                            icon={<IconDropdownIndicator className={cn("transition", { "transform-rotate--180deg": isOpen })} />}
                                                            iconPosition="end"
                                                        />
                                                    )}
                                                    content={closeDropdown => (
                                                        <InfoDropdownList>
                                                            {canChangeAssetLocations &&
                                                                <>
                                                                    <InfoDropdown
                                                                        label={
                                                                            <InfoDropdownListItemButton>
                                                                                <Flex alignItems="center" justifyContent="space-between" gap="0.8rem">
                                                                                    {t('shippingOrders.setFromLocation')}
                                                                                    <FlexItem shrink={0}>
                                                                                        <IconArrowRight className="d-block" />
                                                                                    </FlexItem>
                                                                                </Flex>
                                                                            </InfoDropdownListItemButton>
                                                                        }
                                                                        minWidth="24rem"
                                                                        maxWidth="24rem"
                                                                        placement="right-start"
                                                                        trigger="hover"
                                                                        content={
                                                                            <ShippingOrderModalBulkLocations
                                                                                locations={requestLocations}
                                                                                onSelectLocation={locationId => { closeDropdown(); return handleSelectLocationClickInBulk({ departureLocationId: locationId }) }}
                                                                                onAddLocationClick={createRequestLocation ? () => { closeDropdown(); handleAddLocationClickInBulk(false) } : undefined}
                                                                            />
                                                                        }
                                                                    />
                                                                    <InfoDropdown
                                                                        label={
                                                                            <InfoDropdownListItemButton>
                                                                                <Flex alignItems="center" justifyContent="space-between" gap="0.8rem">
                                                                                    {t('shippingOrders.setToLocation')}
                                                                                    <FlexItem shrink={0}>
                                                                                        <IconArrowRight className="d-block" />
                                                                                    </FlexItem>
                                                                                </Flex>
                                                                            </InfoDropdownListItemButton>
                                                                        }
                                                                        minWidth="24rem"
                                                                        maxWidth="24rem"
                                                                        placement="right-start"
                                                                        trigger="hover"
                                                                        content={
                                                                            <ShippingOrderModalBulkLocations
                                                                                locations={requestLocations}
                                                                                onSelectLocation={locationId => { closeDropdown(); return handleSelectLocationClickInBulk({ arrivalLocationId: locationId }) }}
                                                                                onAddLocationClick={createRequestLocation ? () => { closeDropdown(); handleAddLocationClickInBulk(true) } : undefined}
                                                                            />
                                                                        }
                                                                    />
                                                                </>
                                                            }
                                                            {canDeleteAssets &&
                                                                <InfoDropdownListItemButton onClick={() => { closeDropdown(); handleDeleteAssetsClickInBulk() }}>
                                                                    <Flex alignItems="center" gap="0.8rem">
                                                                        <FlexItem shrink={0}>
                                                                            <IconTrashBin className="d-block" />
                                                                        </FlexItem>
                                                                        {t('shippingOrders.removeAssets')}
                                                                    </Flex>
                                                                </InfoDropdownListItemButton>
                                                            }
                                                        </InfoDropdownList>
                                                    )}
                                                />
                                            </div>
                                        }
                                    </Flex>
                                }
                            </FlexItem>
                            <Flex alignItems="center" justifyContent="flex-end" gap="2.4rem" wrap="wrap">
                                <FlexItem>
                                    {isForwardToShippingCheckboxShown &&
                                        <Checkbox
                                            name="forward-to-shipping"
                                            value="forwardToShipping"
                                            label={t('shippingOrders.forwardOrderToShipping')}
                                            checked={forwardToShipping || false}
                                            disabled={isCartBusy || isForwardToShippingCheckboxDisabled}
                                            onChange={() => setForwardToShipping(!forwardToShipping)}
                                        />
                                    }
                                </FlexItem>
                                <Flex alignItems="center" gap="2.4rem">
                                    <Button
                                        theme="secondary"
                                        label={t('common.cancel')}
                                        onClick={() => close?.()}
                                        disabled={isCartBusy || isSaving}
                                        data-testid="shipping-order-modal-cancel"
                                    />
                                    {isSaveAndPrepareLoadingListButtonShown &&
                                        <Button
                                            label={t('shippingOrders.saveAndPrepareLoadingList')}
                                            onClick={() => handleSubmit({ redirectToPrepareShipping: true })}
                                            theme={isForwardToShippingCheckboxDisabled ? "secondary" : "primary"}
                                            disabled={isCartBusy || isSaving || isSaveAndPrepareLoadingListButtonDisabled}
                                            loading={isSaving}
                                            data-testid="shipping-order-modal-cancel"
                                        />
                                    }
                                    <Button
                                        label={t('common.save')}
                                        onClick={() => handleSubmit()}
                                        theme={isForwardToShippingCheckboxDisabled && onSaved ? "secondary" : "primary"}
                                        disabled={isCartBusy || isSaving}
                                        loading={isSaving}
                                        data-testid="shipping-order-modal-save"
                                    />
                                </Flex>
                            </Flex>
                        </Flex>
                    )}>
                    {isInitCartPending
                        ? <Spinner className="py-4" />
                        : <Form onSubmit={() => handleSubmit()}>
                            <Tabs onSwitchTab={tabIndex => setCurrentTab(tabIndex)}
                                toolbar={currentTab === ShippingOrderEditTab.assets && sort
                                    ? <Flex alignItems="center" className="h-100">
                                        <Button
                                            theme="link"
                                            label={t('shippingOrders.resetToDefaultOrder')}
                                            onClick={() => setSort(null)}
                                        />
                                    </Flex>
                                    : null
                                }>
                                <TabsItem label={t('shippingOrders.basicData')} error={isSubmitted && errorTabs[ShippingOrderEditTab.basicData]}>
                                    <ShippingOrderEditModalBasicData
                                        deliveryTerms={deliveryTerms?.entities}
                                        deliveryTermId={deliveryTerm?.termId}
                                        isDeliveryTermRequired={isDeliveryTermRequired}
                                        tenantWarehouses={tenantWarehouses?.entities}
                                        responsibleWarehouseId={responsibleWarehouse?.id}
                                        isDepartureAndArrivalDateRequired={isDepartureAndArrivalDateRequired}
                                        departureDate={dates?.departureDate || null}
                                        arrivalDate={dates?.arrivalDate || null}
                                        personInCharge={personInCharge}
                                        personInChargePhone={personInChargePhone}
                                        deliveryNoteHeaderText={deliveryNoteHeaderText}
                                        deliveryNoteFooterText={deliveryNoteFooterText}
                                        deliveryNotePrintFeatures={deliveryNotePrintFeatures}
                                        showDeliveryNoteTextSection={cart?.type === ShippingOrderType.delivery}
                                        note={note}
                                        isSubmitted={isSubmitted}
                                        isDisabled={isCartBusy}
                                        validationErrors={validationErrors}
                                        onChange={handleBasicDataChange}
                                    />
                                </TabsItem>
                                <TabsItem label={t('inventory.assets')} error={isSubmitted && errorTabs[ShippingOrderEditTab.assets]}>
                                    <div>
                                        {shippingOrderAssetIds.length > 0 && !!reservation.articles &&
                                            <ShippingOrderModalAssets
                                                isDisabled={isCartBusy}
                                                isSubmitted={isSubmitted}
                                                onDeleteAssetClick={canDeleteAssets ? handleDeleteAsset : undefined}
                                                createRequestLocation={createRequestLocation}
                                                changeAssetDepartureArrivalLocation={canChangeAssetLocations ? changeAssetDepartureArrivalLocation : undefined}
                                                locations={requestLocations}
                                                setAddLocationParams={setAddLocationParams}
                                                sort={sort}
                                                setSort={setSort}
                                                assets={shippingOrderAssetsExtended}
                                                isLoadingAssetDisplayStatuses={isLoadingAssetDisplayStatuses}
                                                selectedAssetIds={selectedAssetIds}
                                                onAssetSelectionChange={onAssetSelectionChange}
                                            />
                                        }
                                        {shippingOrderAssetIds.length === 0 &&
                                            <div className="my-4 p-4 text-center">
                                                <div className="mb-4 white-space-pre-line">
                                                    {t('shippingOrders.noAssetsToDeliver')}
                                                </div>
                                                {canAddAssets &&
                                                    <Button
                                                        label={t('shippingOrders.addAssetsToShippingOrder')}
                                                        disabled={isCartBusy}
                                                        onClick={() => setShowSelectAssetsModal(true)}
                                                    />
                                                }
                                            </div>
                                        }
                                    </div>
                                </TabsItem>
                            </Tabs>
                        </Form>
                    }
                </Modal>
            }
            {errorCode === ErrorCode.forbidden && !!errorDate &&
                <ConfirmModal
                    theme="danger"
                    title={t('common.warning')}
                    maxWidth="640px"
                    onClose={() => leaveWithoutConfirm()}
                    onConfirm={() => setOverrideOwnership(true)}
                    confirmButtonLabel={t('common.confirm')}
                    isBusy={isInitCartPending}>
                    <div className="white-space-pre-line">{t('shippingOrders.updatedByDifferentUserX', { email: errorEmail, date: errorDate.format(t('config.dateLongTimeFormat')) || "" })}</div>
                </ConfirmModal>
            }
            {errorCode === ErrorCode.conflict &&
                <AlertModal
                    title={t('common.attention')}
                    maxWidth="640px"
                    onClose={() => leaveWithoutConfirm()}
                    buttonLabel={t('common.ok')}>
                    <div className="white-space-pre-line">{t('shippingOrders.lockedByDifferentUserX', { email: errorEmail || "", duration: EditSessionDuration.asMinutes() + EditSessionConfirmModalDuration.asMinutes() })}</div>
                </AlertModal>
            }
            {showContinueSessionModal && !!cart &&
                <ContinueSessionModal
                    sessionId={cart.id}
                    duration={EditSessionConfirmModalDuration}
                    onContinue={resetSessionTimeout}
                    onExpire={handleSessionExpired}
                />
            }
            {showSelectAssetsModal && !!cart?.type && canAddAssets &&
                <SelectAssetsForShippingOrderModal
                    shippingOrderType={cart.type}
                    selectedAssetIds={shippingOrderAssetIds}
                    articles={reservation.articles || []}
                    nodeArticleId={reservation.articleId}
                    purpose={reservation.purpose}
                    reservationStart={reservation.time.reservationStart}
                    reservationEnd={reservation.time.reservationEnd}
                    onAssetsSelect={onAssetsSelect}
                    onClose={() => setShowSelectAssetsModal(false)}
                />
            }
            {!!assetsWithFreightUnits &&
                <SelectFreightUnitsModal
                    assetsWithFreightUnits={assetsWithFreightUnits}
                    initialSelectedFreightUnits={freightUnitsToCreate || undefined}
                    onClose={() => { setAssetsWithFreightUnits(null); setFreightUnitsToCreate(null) }}
                    onSave={handleFreightUnitsSelection}
                />
            }
            {showUpdateHomebaseModal &&
                <UpdateHomebaseModal
                    assets={assetsToUpdateHomebase}
                    onClose={() => { setShowUpdateHomebaseModal(false); setFreightUnitsToCreate(null) }}
                    onSave={updateAssetsHomebase}
                />
            }
            {!!addLocationParams &&
                <ShippingOrderAddLocationModal
                    params={addLocationParams}
                    onClose={handleAddLocationModalClose}
                    createRequestLocation={createRequestLocation}
                />
            }
        </>
    )
}

export default ShippingOrderEditModal
